import React, { useRef, useMemo } from 'react'
import { useParams } from 'react-router-dom'

import {
  CanvasRendererProvider,
  useStatus,
} from '@sketch-hq/sketch-web-renderer'
import { useForTablet, ErrorMessage, LoadingState } from '@sketch/components'
import {
  AnnotationQueryVariablesProvider,
  RouteParams,
  useUserSignedIn,
} from '@sketch/modules-common'
import { IS_EMBEDDED } from '@sketch/constants'

import { CanvasInteractionsProvider } from '../../../canvas/components/CanvasInteractions'
import {
  CanvasLeftPanel,
  CanvasLeftPanelResizingProvider,
} from '../../../canvas/components/CanvasLeftPanel'
import { SelectedFrameProvider } from '../../../canvas/components/SelectedFrameContext'
import { FrameInspectorProvider } from '../../FrameView/components/FrameInspectorProvider'
import { FrameSelectorForPreview } from '../../FrameView/components/FrameSelector/FrameSelectorForPreview'
import { SectionSelectorForPreview } from '../../SectionView/components/SectionSelector'
import { FloatingZoom } from '../../FrameView/components/FloatingZoom'
import { Panel } from '../../components/Panel'
import { DocumentSidebarLayoutExtraProps } from '../../components/DocumentSidebarLayout'
import { INSPECTOR_SEGMENT } from '../../constants'
import { useGetActiveSidebarTab } from '../../components/ShareSidebarTabContext'
import { GridsAndLayoutsProvider } from '../../components/GridsAndLayoutsProvider'
import { DraftAnnotationNotifier } from '../../../annotations/components'
import { usePlaceDraftAnnotation } from '../../../annotations/hooks'
import { ReactComponent as DocumentIcon } from '@sketch/icons/document-64'

import { SharePageCanvas } from '../../PageCanvasView/components/SharePageCanvas'
import PageCanvasViewHeader from '../../PageCanvasView/components/PageCanvasViewHeader'
import { PageSelectorForPreview } from '../../PageCanvasView/components/PageSelector'
import { PageCanvasSupportGate } from '../../PageCanvasView/components/PageCanvasSupportGate'
import {
  ErrorWrapper,
  StyledCanvasViewWrapper,
} from '../../PageCanvasView/PageCanvasView.styles'
import {
  DocumentMobileToolbar,
  isMobileToolboxItem,
} from '../../components/DocumentMobileToolbar'
import { LoadingMessage } from '../../components/LoadingMessage'

import { useCanvasZoom } from '../../../../components/WebRendererCanvas/hooks'
import { useVersioning } from '../../../versioning'
import { usePreview } from './PreviewContext'
import { usePreviewSingleEntity } from './usePreviewSingleEntity'
import { PreviewAnnotationOverlayContext } from './PreviewAnnotationOverlayContext'
import type { GetPreviewDetailsQuery } from '@sketch/gql-types'
import { noop } from '@sketch/utils'

type PageCanvasViewProps = {
  preview: NonNullable<GetPreviewDetailsQuery['preview']>
} & DocumentSidebarLayoutExtraProps

const PageCanvasMobileToolBox = () => {
  const canvasStatus = useStatus()
  const [zoom, setPageZoom] = useCanvasZoom()
  const [placeDraftAnnotation, setPlaceDraftAnnotation] =
    usePlaceDraftAnnotation() || []

  const MOBILE_TOOLBAR_ITEMS = [
    'doc-info',
    'search',
    'zoom',
    'add-comment',
    'versions',
    'comments',
  ].filter(isMobileToolboxItem)

  return (
    <DocumentMobileToolbar
      items={MOBILE_TOOLBAR_ITEMS}
      zoom={zoom}
      setZoom={setPageZoom}
      isDraftAnnotationEnabled={placeDraftAnnotation}
      setPlaceDraftAnnotation={setPlaceDraftAnnotation}
      disableAddAnnotationButton={canvasStatus?.type !== 'READY'}
    />
  )
}

type PageCanvasContentProps = {
  preview: NonNullable<GetPreviewDetailsQuery['preview']>
  permanentPageId: string
} & DocumentSidebarLayoutExtraProps

/**
 * Inner component rendered inside CanvasRendererProvider so that
 * usePreviewSingleEntity (which calls into the renderer) can be called here.
 * Keeping CanvasRendererProvider mounted across page↔entity navigation
 * allows the renderer to animate between views.
 */
function PageCanvasContent({
  preview,
  permanentPageId,
  HeaderPortal,
  SidebarRightPortal,
  toggleSidebarRight,
  isSidebarRightOpen,
}: PageCanvasContentProps) {
  const {
    isShowingSingleFrame,
    frameUUID,
    isShowingSingleSection,
    sectionUUID,
    entityUUID,
  } = usePreviewSingleEntity()
  const { pageUUID } = useParams<Partial<RouteParams<'PREVIEW_FRAME_VIEW'>>>()

  const previewContext = usePreview()
  const { currentVersion } = useVersioning()
  const isSignedIn = useUserSignedIn()
  const isTabletAndBigger = useForTablet()

  const activeSidebarTab = useGetActiveSidebarTab()
  const isInspectPanelOpen = activeSidebarTab === INSPECTOR_SEGMENT

  const canvasRef = useRef<HTMLDivElement>(null)

  // In the frame and Section focus views the pageUUID comes from the URL;
  // otherwise fall back to the page from context (or the first page).
  const isShowingSingleEntity = isShowingSingleFrame || isShowingSingleSection
  const activePageId =
    (isShowingSingleEntity ? pageUUID : null) ?? permanentPageId

  const subjects = useMemo(
    () => [{ type: 'PAGE' as const, permanentId: activePageId }],
    [activePageId]
  )

  const disableAnnotations = !preview.commentsEnabled || !isSignedIn
  const userCanSeeComments =
    (previewContext && preview.commentsEnabled) || !previewContext
  const presentationStatus = preview.presentationStatus ?? 'PENDING'
  const isProcessingPresentationFile = presentationStatus === 'PENDING'
  const showPagesList = !IS_EMBEDDED

  const leftPanelNavigation = isShowingSingleSection ? (
    <SectionSelectorForPreview />
  ) : isShowingSingleFrame ? (
    <FrameSelectorForPreview />
  ) : (
    <PageSelectorForPreview previewIdentifier={preview.identifier} />
  )

  return (
    // In a focus view: scope the inspector layer tree to the focused entity's
    // children by passing rootElementUUID. autoSelectRootInitially then selects
    // that element automatically (it's now the root of the inspector tree).
    <FrameInspectorProvider
      autoSelectRootInitially={isShowingSingleEntity}
      rootElementUUID={entityUUID}
      key={entityUUID ?? activePageId}
    >
      <AnnotationQueryVariablesProvider subjects={subjects} isPreview>
        <PreviewAnnotationOverlayContext
          disabled={disableAnnotations}
          hidden={isInspectPanelOpen}
          isViewingLatestVersion={true}
          permanentPageId={activePageId}
          frameUUID={frameUUID}
          sectionUUID={sectionUUID}
        >
          <HeaderPortal>
            <PageCanvasViewHeader
              setSidebarLeftOpen={noop}
              toggleSidebarRight={toggleSidebarRight}
              isSidebarRightOpen={isSidebarRightOpen}
              isViewingLatestVersion={true}
              isViewingStarredVersion={false}
              hasPendingPatches={false}
              userCanOpenInApp={false}
              currentVersion={currentVersion}
            />
            <Panel
              userCanSeeComments={userCanSeeComments}
              userCanInspect={preview.inspectEnabled}
              SidebarRightPortal={SidebarRightPortal}
              version={currentVersion}
            />
          </HeaderPortal>

          <FloatingZoom />

          <PageCanvasSupportGate>
            {isProcessingPresentationFile ? (
              <LoadingState
                data-testid="loader-preview-canvas"
                type="color-icon"
                size="48px"
                message={<LoadingMessage />}
              />
            ) : (
              <>
                {activePageId ? (
                  <StyledCanvasViewWrapper>
                    {showPagesList ? (
                      <CanvasLeftPanel
                        isInspectPanelOpen={isInspectPanelOpen}
                        stateKey={`${preview.identifier}:${activePageId}`}
                        navigationComponent={leftPanelNavigation}
                      />
                    ) : null}
                    <SharePageCanvas
                      ref={canvasRef}
                      isViewingLatestVersion={true}
                      permanentPageIdentifier={activePageId}
                      // The focused entity - frame or Section - becomes the
                      // renderer's scene root, which isolates it and scopes the
                      // canvas overlays to what it contains: a Section keeps the
                      // overlays of its child entities so you can drill into it,
                      // while a frame contains none and shows no overlays.
                      rootEntityUUID={entityUUID}
                    />
                    {isTabletAndBigger && <DraftAnnotationNotifier />}
                  </StyledCanvasViewWrapper>
                ) : (
                  <ErrorWrapper>
                    <ErrorMessage
                      title="No pages in this preview"
                      icon={<DocumentIcon />}
                      iconSize="medium"
                    >
                      No pages have been added here yet.
                    </ErrorMessage>
                  </ErrorWrapper>
                )}
              </>
            )}
          </PageCanvasSupportGate>
          {!IS_EMBEDDED && !isTabletAndBigger && <PageCanvasMobileToolBox />}
        </PreviewAnnotationOverlayContext>
      </AnnotationQueryVariablesProvider>
    </FrameInspectorProvider>
  )
}

export function PageCanvasView(props: PageCanvasViewProps) {
  const { preview, ...layoutProps } = props
  const previewContext = usePreview()

  const firstPageUUID = preview.pages.entries[0]?.uuid
  const permanentPageId =
    previewContext?.currentPage?.uuid || firstPageUUID || ''

  return (
    <CanvasRendererProvider>
      <CanvasInteractionsProvider>
        <SelectedFrameProvider>
          <CanvasLeftPanelResizingProvider>
            <GridsAndLayoutsProvider>
              <PageCanvasContent
                preview={preview}
                permanentPageId={permanentPageId}
                {...layoutProps}
              />
            </GridsAndLayoutsProvider>
          </CanvasLeftPanelResizingProvider>
        </SelectedFrameProvider>
      </CanvasInteractionsProvider>
    </CanvasRendererProvider>
  )
}
